"use client";

import React from "react";
import { MapPin, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const experiences = [{
  id: 1,
  title: "Tayrona National Park",
  description: "Discover pristine beaches where the dense jungle canopy meets the crystal-clear Caribbean Sea. A sanctuary of biodiversity and ancient heritage.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/tayrona_coastal_jungle.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/tayrona_coastal_jungle.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/tayrona_coastal_jungle.png-wvc-sizes",
  tag: "Coastal Jungle",
  link: "/PalominoPage"
}, {
  id: 2,
  title: "Palomino River Expedition",
  description: "Float down the serene Palomino River surrounded by lush tropical flora, culminating where the fresh water merges with the ocean waves.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/palomino_river_tubing.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/palomino_river_tubing.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/palomino_river_tubing.png-wvc-sizes",
  tag: "River Adventure",
  link: "/PalominoPage"
}, {
  id: 3,
  title: "Sierra Nevada Foothills",
  description: "Trek through the mystical foothills of the world's highest coastal mountain range, engaging with local communities and untouched nature.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/sierra_nevada_trek.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/sierra_nevada_trek.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/sierra_nevada_trek.png-wvc-sizes",
  tag: "Mountain Trek",
  link: "/PalominoPage"
}, {
  id: 7,
  title: "ATV Tours",
  description: "Feel the adrenaline on an off-road adventure through rugged jungle paths and hidden coastal trails.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/atv_tours_adventure.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/atv_tours_adventure.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/atv_tours_adventure.png-wvc-sizes",
  tag: "Off-Road",
  link: "/PalominoPage"
}, {
  id: 8,
  title: "Paddleboarding & Kayaking",
  description: "Navigate the calm coastal waters or river bends at your own pace, connecting deeply with the aquatic environment.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/paddleboarding_kayaking_palomino.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/paddleboarding_kayaking_palomino.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/paddleboarding_kayaking_palomino.png-wvc-sizes",
  tag: "Water Sports",
  link: "/PalominoPage"
}, {
  id: 9,
  title: "Surf Lessons",
  description: "Catch your first wave or refine your technique with expert instructors on Palomino's energetic surf breaks.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/surf_lessons_caribbean.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/surf_lessons_caribbean.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/surf_lessons_caribbean.png-wvc-sizes",
  tag: "Surfing",
  link: "/PalominoPage"
}, {
  id: 10,
  title: "Motorbike Ride",
  description: "Explore the scenic coastal highway and mountain access roads on a thrilling motorbike excursion.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/motorbike_ride_scenic.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/motorbike_ride_scenic.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/motorbike_ride_scenic.png-wvc-sizes",
  tag: "Road Trip",
  link: "/PalominoPage"
}, {
  id: 11,
  title: "Horseback Riding",
  description: "Traverse the golden beaches, mountain ridges, and traditional fincas on a majestic equestrian journey.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/horseback_riding_beach.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/horseback_riding_beach.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/horseback_riding_beach.png-wvc-sizes",
  tag: "Equestrian",
  link: "/PalominoPage"
}, {
  id: 12,
  title: "Cacao Farms",
  description: "Learn the secrets of artisanal chocolate making, from bean to bar, in the heart of the Sierra Nevada foothills.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/cacao_farms_tour.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/cacao_farms_tour.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/cacao_farms_tour.png-wvc-sizes",
  tag: "Cultural",
  link: "/PalominoPage"
}, {
  id: 13,
  title: "Quebrada Valencia",
  description: "Hike to these stunning natural waterfalls and cool off in the refreshing stone pools surrounded by giant ferns.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/quebrada_valencia_waterfalls.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/quebrada_valencia_waterfalls.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/quebrada_valencia_waterfalls.png-wvc-sizes",
  tag: "Waterfalls",
  link: "/PalominoPage"
}, {
  id: 14,
  title: "Indigenous Community Visits",
  description: "A rare opportunity to learn from the Kogui and Arhuaco people, understanding their deep spiritual connection to the Earth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/indigenous_community_visit.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/indigenous_community_visit.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/indigenous_community_visit.png-wvc-sizes",
  tag: "Heritage",
  link: "/PalominoPage"
}, {
  id: 15,
  title: "Authentic Local Traditions",
  description: "Immerse yourself in the daily rhythms of Palomino, from traditional fishing techniques to local culinary secrets.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/local_traditions_palomino.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/local_traditions_palomino.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/local_traditions_palomino.png-wvc-sizes",
  tag: "Authentic",
  link: "/PalominoPage"
}, {
  id: 16,
  title: "Artisan Partnerships",
  description: "Meet the master weavers and craftsmen preserving ancestral techniques, and support sustainable local economies.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/artisan_partnerships_colombia.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/artisan_partnerships_colombia.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/artisan_partnerships_colombia.png-wvc-sizes",
  tag: "Craftsmanship",
  link: "/PalominoPage"
}, {
  id: 17,
  title: "Educational Exchanges",
  description: "Participate in meaningful dialogues and workshops that bridge cultures and foster mutual understanding.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/educational_exchange_program.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/educational_exchange_program.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/educational_exchange_program.png-wvc-sizes",
  tag: "Education",
  link: "/PalominoPage"
}];
function ExperienceCard({
  experience,
  index
}: {
  experience: typeof experiences[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="bg-transparent border-none rounded-lg p-0 gap-6 group h-full flex flex-col">
        <AspectRatio ratio={4 / 3} className="overflow-hidden rounded-lg bg-background/5">
          <img src={experience.image} data-wvc-srcset={experience.imageSrcset} data-wvc-sizes={experience.imageSizes} alt={experience.title} loading="lazy" className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-700 ease-in-out opacity-90 group-hover:opacity-100" />
        </AspectRatio>
        <CardContent className="p-0 flex flex-col flex-grow">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4 text-secondary" />
            <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-widest border-secondary/30 text-secondary rounded-lg">
              {experience.tag}
            </Badge>
          </div>
          <div className="w-8 h-px bg-primary mb-5 transition-all duration-500 group-hover:w-16"></div>
          <h3 className="font-default text-3xl text-foreground mb-3 leading-tight">
            {experience.title}
          </h3>
          <p className="text-foreground/70 text-sm leading-relaxed mb-6 flex-grow font-sans">
            {experience.description}
          </p>
          <div className="mt-auto">
            <Button asChild variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent group/btn">
              <Link to={experience.link} className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider">
                Explore Experience
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function AdventureNatureExperience() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="adventurenatureexperience" className="bg-background">
      <div className="bg-card py-24 md:py-32 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          
          <header ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="text-primary font-mono tracking-widest uppercase text-xs md:text-sm mb-4 block">
              Curated Expeditions
            </span>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight mb-6">Adventure &amp; Culture</h2>
            <p className="text-foreground/70 text-base md:text-lg font-sans max-w-2xl mx-auto leading-relaxed">Discover Palomino's most breathtaking landscapes through our meticulously crafted journeys, designed for the discerning traveler seeking authentic connection with nature. We believe in traveling the world while preserving its soul. Discover the profound wisdom of local cultures through our carefully curated, respectful encounters.</p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-8 lg:gap-12">
            {experiences.map((experience, index) => <ExperienceCard key={experience.id} experience={experience} index={index} />)}
          </div>

        </div>
      </div>
    </section>;
}